// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { fShortenNumber } from '../../utils/formatNumber';

export interface FeedbackConversation {
  id: string;
  username: string;
  query: string;
  response: string;
  feedback: 'good' | 'partiallyCorrect' | 'fullWrong' | 'noFeedback' | 'other';
  feedbackMessage?: string;
  timestamp: string;
  tokens: number;
  cost: number;
  projectName?: string;
}

interface FeedbackDetailsPageProps {
  feedbackType: string;
  conversations: FeedbackConversation[];
  onBack: () => void;
  contextLabel?: string;
  loading?: boolean;
}

const feedbackConfig = {
  good: { label: 'Good', color: 'success', icon: 'mdi:thumb-up' },
  partiallyCorrect: { label: 'Partially Wrong', color: 'warning', icon: 'mdi:thumbs-up-down' },
  fullWrong: { label: 'Full Wrong', color: 'error', icon: 'mdi:thumb-down' },
  noFeedback: { label: 'No Feedback', color: 'default', icon: 'mdi:comment-remove-outline' },
  other: { label: 'Other', color: 'info', icon: 'mdi:help-circle-outline' },
};

export default function FeedbackDetailsPage({
  feedbackType,
  conversations,
  onBack,
  contextLabel,
  loading = false,
}: FeedbackDetailsPageProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const config = feedbackConfig[feedbackType] || feedbackConfig.good;
  // Cache to avoid scanning the array twice per render
  const hasProject = conversations.some((c) => c.projectName);

  // Reset to page 0 whenever the feedback type changes (avoids out-of-range pagination)
  useEffect(() => {
    setPage(0);
  }, [feedbackType]);

  const paginatedConversations = conversations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleDownload = () => {
    const headers = [
      '#',
      'User',
      'Query',
      'Response',
      'Feedback',
      'Feedback Message',
      'Project',
      'Tokens',
      'Cost',
      'Timestamp',
    ];
    const rows = conversations.map((c, i) => [
      i + 1,
      c.username,
      `"${c.query.replace(/"/g, '""')}"`,
      `"${c.response.replace(/"/g, '""')}"`,
      config.label,
      `"${(c.feedbackMessage || '').replace(/"/g, '""')}"`,
      c.projectName || '',
      c.tokens,
      `$${c.cost.toFixed(2)}`,
      new Date(c.timestamp).toLocaleString(),
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${feedbackType}_conversations.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Button variant="outlined" startIcon={<Iconify icon="mdi:arrow-left" />} onClick={onBack} size="small">
          Back{contextLabel ? ` to ${contextLabel}` : ''}
        </Button>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette[config.color]?.main || theme.palette.grey[400], 0.12),
            color: theme.palette[config.color]?.main || theme.palette.grey[400],
          }}
        >
          <Iconify icon={config.icon} width={28} />
        </Box>
        <Box>
          <Typography variant="h5">{config.label} Conversations</Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {loading ? 'Loading conversations...' : `${conversations.length} conversations`}
          </Typography>
        </Box>
      </Stack>

      <Card>
        <CardHeader
          title={`${config.label} Feedback`}
          subheader={loading ? 'Loading...' : `${conversations.length} total conversations`}
          action={
            <Tooltip title="Download CSV">
              <IconButton onClick={handleDownload} disabled={loading || conversations.length === 0}>
                <Iconify icon="mdi:download" />
              </IconButton>
            </Tooltip>
          }
        />
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ px: 2, overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Query</TableCell>
                    <TableCell>Response</TableCell>
                    <TableCell>Feedback Message</TableCell>
                    {hasProject && <TableCell>Project</TableCell>}
                    <TableCell align="right">Tokens</TableCell>
                    <TableCell align="right">Cost</TableCell>
                    <TableCell align="right">Time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedConversations.map((conv, index) => (
                    <TableRow key={conv.id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{conv.username}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200, md: 250 } }}>
                          {conv.query}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 200, md: 250 } }}>
                          {conv.response}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 120, sm: 180, md: 220 } }}>
                          {conv.feedbackMessage || '—'}
                        </Typography>
                      </TableCell>
                      {hasProject && (
                        <TableCell>
                          <Chip label={conv.projectName} size="small" variant="outlined" />
                        </TableCell>
                      )}
                      <TableCell align="right">{fShortenNumber(conv.tokens)}</TableCell>
                      <TableCell align="right">${conv.cost.toFixed(2)}</TableCell>
                      <TableCell align="right">
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {new Date(conv.timestamp).toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={conversations.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
